/**
 * Data Source Service - Handles API interactions for external data sources and integrations
 */
import { post, put, get } from "../utils/apiUtils";


// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL = `/api/v1/mms_persistence/data_source`;

/**
 * Get all data sources with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of data sources
 */
export const getDataSources = async (filter = {}, sort = {}, query = null) => {
  try {
    const response = await post(`${API_URL}/fetch?${query}`, { filter: filter, sort });
    return response;
  } catch (error) {
    console.error('Error fetching connector:', error);
    throw error;
  }
};

/**
 * Create a new data source
 * @param {Object} sourceData - Data source data
 * @returns {Promise<Object>} Promise resolving to created data source
 */
export const createDataSource = async (sourceData) => {
  try {
    const response = await post(API_URL, sourceData);
    return response;
  } catch (error) {
    console.error('Error creating data source:', error);
    throw error;
  }
};

/**
 * Update an existing data source
 * @param {string} sourceId - Data source ID
 * @param {Object} sourceData - Updated data source data
 * @returns {Promise<Object>} Promise resolving to updated data source
 */
export const updateDataSource = async (sourceData) => {
  try {
    const response = await put(`${API_URL}`, sourceData);
    return response;
  } catch (error) {
    console.error(`Error updating data source:`, error);
    throw error;
  }
};

// /**
//  * Delete a data source
//  * @param {string} sourceId - Data source ID
//  * @returns {Promise<void>} Promise resolving when data source is deleted
//  */
// export const deleteDataSource = async (sourceId) => {
//   try {
//     await delete(`${API_URL}/${sourceId}`);
//   } catch (error) {
//     console.error(`Error deleting data source ${sourceId}:`, error);
//     throw error;
//   }
// };

/**
 * Test a data source connection
 * @param {string} sourceId - Data source ID
 * @returns {Promise<Object>} Promise resolving to connection test result
 */
export const testDataSourceConnection = async (sourceId) => {
  try {
    const response = await post(`${API_URL}/${sourceId}/test-connection`);
    return response.data;
  } catch (error) {
    console.error(`Error testing connection for data source ${sourceId}:`, error);
    throw error;
  }
};

/**
 * Get data source schema
 * @param {string} sourceId - Data source ID
 * @returns {Promise<Object>} Promise resolving to data source schema
 */
export const getDataSourceSchema = async (sourceId) => {
  try {
    const response = await get(`${API_URL}/${sourceId}/schema`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching schema for data source ${sourceId}:`, error);
    throw error;
  }
};

/**
 * Query a data source
 * @param {string} sourceId - Data source ID
 * @param {Object} queryData - Query parameters
 * @returns {Promise<Object>} Promise resolving to query results
 */
export const queryDataSource = async (sourceId, queryData) => {
  try {
    const response = await post(`${API_URL}/${sourceId}/query`, queryData);
    return response.data;
  } catch (error) {
    console.error(`Error querying data source ${sourceId}:`, error);
    throw error;
  }
};

/**
 * Get data source sync status
 * @param {string} sourceId - Data source ID
 * @returns {Promise<Object>} Promise resolving to sync status
 */
export const getDataSourceSyncStatus = async (sourceId) => {
  try {
    const response = await get(`${API_URL}/${sourceId}/sync-status`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching sync status for data source ${sourceId}:`, error);
    throw error;
  }
};

/**
 * Trigger data source sync
 * @param {string} sourceId - Data source ID
 * @param {Object} options - Sync options
 * @returns {Promise<Object>} Promise resolving to sync result
 */
export const syncDataSource = async (sourceId, options = {}) => {
  try {
    const response = await post(`${API_URL}/${sourceId}/sync`, options);
    return response.data;
  } catch (error) {
    console.error(`Error syncing data source ${sourceId}:`, error);
    throw error;
  }
};

/**
 * Get data source credentials
 * @param {string} sourceId - Data source ID
 * @returns {Promise<Object>} Promise resolving to credentials
 */
export const getDataSourceCredentials = async (sourceId) => {
  try {
    const response = await get(`${API_URL}/${sourceId}/credentials`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching credentials for data source ${sourceId}:`, error);
    throw error;
  }
};

/**
 * Update data source credentials
 * @param {string} sourceId - Data source ID
 * @param {Object} credentials - Updated credentials
 * @returns {Promise<Object>} Promise resolving to updated credentials
 */
export const updateDataSourceCredentials = async (sourceId, credentials) => {
  try {
    const response = await put(`${API_URL}/${sourceId}/credentials`, credentials);
    return response.data;
  } catch (error) {
    console.error(`Error updating credentials for data source ${sourceId}:`, error);
    throw error;
  }
};

/**
 * Get data source types
 * @returns {Promise<Array>} Promise resolving to array of data source types
 */
export const getDataSourceTypes = async () => {
  try {
    const response = await get(`${API_URL}/types`);
    return response.data;
  } catch (error) {
    console.error('Error fetching data source types:', error);
    throw error;
  }
};

/**
 * Get data source types
 * @returns {Promise<Array>} Promise resolving to array of data source types
 */

export const getDataSourceCount = async (filter = {}) => {
   try {
     const response = await post(`${API_URL}/count`, filter);
     return response.count;
   } catch (error) {
     console.error('Error fetching knowledge bases:', error);
     throw error;
   }
};

export default {
  getDataSources,
  createDataSource,
  updateDataSource,
  testDataSourceConnection,
  getDataSourceSchema,
  queryDataSource,
  getDataSourceSyncStatus,
  syncDataSource,
  getDataSourceCredentials,
  updateDataSourceCredentials,
  getDataSourceTypes
};

